import { IconPlus, IconX } from "@/components/Icons";
import { floorOf, hasRoom, isSingle, rowsFor } from "@/lib/appointments";
import type { FormOption, SlotOptions } from "@/lib/api/types";

import styles from "./FixtureForm.module.css";

/**
 * One appointment slot, filled as many times as it allows.
 *
 * A slot has bounds, and until now the form ignored them and drew exactly one
 * picker. That made three of the four sports' headline cases unenterable: a
 * syndicate is routinely forty owners, a bout is scored by three judges, a Test
 * has two on-field umpires. Every one of them saved fine through the API and
 * could not be typed into the screen.
 *
 * So the bounds decide the shape:
 *
 * * ``max === 1`` — one picker, no buttons. Identical to what it replaced, which
 *   is most slots and should not have grown a paperclip.
 * * anything else — a picker per appointment, a remove on each above the floor,
 *   and an add while there is room. ``max === null`` means no ceiling.
 *
 * The floor is drawn empty rather than hidden: three blank judge rows say "this
 * needs three" better than a hint under a single dropdown.
 */
export function Appointments({
  slot,
  chosen,
  onChange,
  idPrefix,
  options,
}: {
  slot: SlotOptions;
  /** One participant code per appointment. Empty strings are unfilled rows. */
  chosen: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
  /** Overrides the slot's own options — the lineup pickers narrow by side. */
  options?: FormOption[];
}) {
  const pool = options ?? slot.options;
  const floor = floorOf(slot);
  const rows = rowsFor(slot, chosen);
  const single = isSingle(slot);
  const room = hasRoom(slot, rows);

  const set = (index: number, value: string) =>
    onChange(rows.map((v, i) => (i === index ? value : v)));

  return (
    <div className={single ? styles.field : styles.slotGroup}>
      {rows.map((value, index) => {
        const id = `${idPrefix}-${slot.role}-${index}`;
        return (
          <div className={styles.field} key={id}>
            <label className={styles.label} htmlFor={id}>
              {single ? slot.label : `${slot.label} ${index + 1}`}
              {index < (slot.min ?? 0) && <span className={styles.required}> *</span>}
            </label>
            <div className={styles.slotPicker}>
              <select
                id={id}
                className={`${styles.control} ${styles.select}`}
                value={value}
                onChange={(e) => set(index, e.target.value)}
              >
                <option value="">{pool.length ? "Choose…" : "Nothing to choose"}</option>
                {pool.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </select>
              {!single && rows.length > floor && (
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={`Remove ${slot.label.toLowerCase()} ${index + 1}`}
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                >
                  <IconX size={15} />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {!single && room && (
        <button
          type="button"
          className={styles.addSlot}
          onClick={() => onChange([...rows, ""])}
        >
          <IconPlus size={13} /> Add another {slot.label.toLowerCase()}
        </button>
      )}
    </div>
  );
}
